import {
  convertDistance,
  getCompassDirection,
  getGreatCircleBearing,
  getPreciseDistance,
} from 'geolib';
import { Set } from 'immutable';
import { round } from 'lodash-es';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ViewModelService } from 'src/app/shared/view-model.service';
import { PassengerType } from 'src/graphql/generated/graphql.generated';
import { ManifestEntryViewModel } from 'src/models/manifest-entry-view-model';
import { TripViewModel } from 'src/models/trip-view-model';
import { WaypointViewModel } from 'src/models/waypoint-view-model';
import { selectEstimatedDurationsById } from 'src/store/entityStores/estimated-duration-selectors';
import { selectFlightFollowingReportsByTripLegId } from 'src/store/entityStores/flight-following-report-selectors';
import {
  selectManifestEntriesByTripLegId,
  selectManifestEntryById,
  selectManifestEntryByLegAndPassengerId,
} from 'src/store/entityStores/manifest-selectors';
import { TripLegRecord, TripLegViewModelBase } from 'src/store/generated/trip-leg-record.generated';
import { hasLatLng } from 'src/store/model-helpers';
import { getLatLng } from 'src/util/mapping/trip-leg-model-record';
import { filterNull } from 'src/util/rxjs/operators/filter-null';
// eslint-disable-next-line @typescript-eslint/naming-convention
export class TripLegViewModel_DEPRECATED extends TripLegViewModelBase {
  get flightPlan$() {
    return this._model.store
      .select(selectFlightFollowingReportsByTripLegId(this.id))
      .pipe(filterNull());
  }

  constructor(tripLeg: TripLegRecord, private readonly _model: ViewModelService) {
    super(tripLeg);
  }
}

export class TripLegViewModel extends TripLegViewModelBase {
  public readonly trip: TripViewModel;
  public readonly manifestEntryIds$: Observable<Set<string>>;
  public readonly manifestEntries$: Observable<ManifestEntryViewModel[]>;

  get startingLatLng() {
    return this.startingWaypoint != null && hasLatLng(this.startingWaypoint)
      ? getLatLng(this.startingWaypoint)
      : undefined;
  }

  get endingLatLng() {
    return this.endingWaypoint != null && hasLatLng(this.endingWaypoint)
      ? getLatLng(this.endingWaypoint)
      : undefined;
  }

  get distance() {
    return this.startingLatLng != null && this.endingLatLng != null
      ? getPreciseDistance(this.startingLatLng, this.endingLatLng)
      : undefined;
  }

  get miles() {
    return this.distance != null ? round(convertDistance(this.distance, 'mi'), 0) : undefined;
  }

  get nauticalMiles() {
    return this.distance != null ? round(convertDistance(this.distance, 'sm'), 0) : undefined;
  }

  get direction() {
    return this.distance != null && this.startingLatLng != null && this.endingLatLng != null
      ? getCompassDirection(this.startingLatLng, this.endingLatLng)
      : undefined;
  }

  get bearing() {
    return this.startingLatLng != null && this.endingLatLng != null
      ? Math.floor(getGreatCircleBearing(this.startingLatLng, this.endingLatLng))
      : undefined;
  }

  get hasGeo() {
    return this.startingLatLng != null && this.endingLatLng != null;
  }

  get officialDuration$() {
    return this.officialEteId == null
      ? of(undefined)
      : this._model.store.select(selectEstimatedDurationsById(this.officialEteId));
  }

  get calculatedDuration$() {
    return this.calculatedEteId == null
      ? of(undefined)
      : this._model.store.select(selectEstimatedDurationsById(this.calculatedEteId));
  }

  get isLoaded$() {
    return this.manifestEntries$.pipe(
      filterNull(),
      map((entries) => entries.find((e) => e.passenger.type === PassengerType.Patient)),
      map((patientEntry) => patientEntry != null),
    );
  }

  get isConfirmed$() {
    return this.manifestEntries$.pipe(
      map((entries) => {
        if (entries == null) {
          return false;
        }

        // if there is at least one record not confirmed, return false
        const nonConfirmedExists =
          entries.find((m) => !m.isConfirmed && !m.passenger.isPilot) != null;
        if (nonConfirmedExists) {
          return false;
        }
        return true;
      }),
    );
  }

  constructor(
    tripLeg: TripLegRecord,
    trip: TripViewModel,
    public readonly startingWaypoint: WaypointViewModel | undefined,
    public readonly endingWaypoint: WaypointViewModel | undefined,
    private readonly _model: ViewModelService,
  ) {
    super(tripLeg);
    this.trip = trip;
    this.manifestEntryIds$ = _model.store
      .select(selectManifestEntriesByTripLegId(this.id, this.tripId))
      .pipe(map((entries) => entries.map((e) => e.id).toSet()));

    this.manifestEntries$ = this.manifestEntryIds$.pipe(
      switchMap((entryIds) => {
        const ids = entryIds.toArray();
        return ids.length === 0
          ? of([])
          : combineLatest(...ids.map((entryId) => this.getManifestEntry(entryId)));
      }),
    );
  }

  public getManifestEntry(id: string) {
    return this._model.store.select(selectManifestEntryById(id)).pipe(
      filterNull(),
      switchMap((e) => {
        return this.trip.getPassenger(e.passengerId).pipe(
          filterNull(),
          map((passenger) => new ManifestEntryViewModel(e, this, passenger, this._model)),
        );
      }),
    );
  }

  public getManifestEntryByPassenger(passengerId: string) {
    return this._model.store
      .select(selectManifestEntryByLegAndPassengerId(this.id, passengerId))
      .pipe(
        switchMap((manifest) =>
          manifest == null ? of(undefined) : this.getManifestEntry(manifest.id),
        ),
      );
  }
}
